/**
 * The dry run and the commit, in batches (KPR-1899).
 *
 * Ingest takes 200 rows and 5 MB a call, and answers every row. `send(body,
 * config)` is the one way out: POST …/ingest from the page, and for a bundle's
 * Bring your data step (KPR-1900) the step's own route, which takes the same
 * body. Nothing here knows which.
 *
 * A LOST ANSWER IS NOT "NOTHING HAPPENED". The API's route timeout leaves its
 * handler running, so a batch that got no answer may have landed. Each row
 * carries an external id of its own run, so sending the batch again is safe:
 * a row that landed answers `duplicate`, which on a re-send means it is there.
 */

export const BATCH_ROWS = 200;
// Under ingest's 5 MB, leaving room for the envelope.
export const BATCH_BYTES = 4 * 1024 * 1024;
const ATTEMPTS = 3;
const BACKOFF_MS = [1000, 3000];
const MAX_WAIT_MS = 30000;

const SIGNATURE_CODE = 'attestation_required';
const PLAN_CODE = 'plan_limit';

/** A string's size as UTF-8, which is what the route counts: "日本語" is nine bytes, not three. */
function utf8Bytes(text) {
	let bytes = 0;
	for (let i = 0; i < text.length; i += 1) {
		const code = text.charCodeAt(i);
		if (code < 0x80) bytes += 1;
		else if (code < 0x800) bytes += 2;
		else bytes += 3;
	}
	return bytes;
}

export function batchesOf(rows) {
	const batches = [];
	let batch = [];
	let bytes = 0;
	rows.forEach(row => {
		const size = utf8Bytes(JSON.stringify(row.elements));
		if (batch.length && (batch.length >= BATCH_ROWS || bytes + size > BATCH_BYTES)) {
			batches.push(batch);
			batch = [];
			bytes = 0;
		}
		batch.push(row);
		bytes += size;
	});
	if (batch.length) batches.push(batch);
	return batches;
}

const externalIdOf = (run, row) => `${run.key}-${row.number}`;

function bodyOf(batch, { card, run, dryRun }) {
	return {
		mode: 'create',
		dryRun,
		strict: true,
		source: { system: run.system, ...(run.ref ? { ref: run.ref } : {}) },
		// An import is its committed calls; a dry run is never one of them.
		...(dryRun ? {} : { importId: run.importId }),
		items: batch.map(row => ({ card, elements: row.elements, source: { externalId: externalIdOf(run, row) } })),
	};
}

const defaultWait = ms => new Promise(resolve => { setTimeout(resolve, ms); });

const isRetryable = error => {
	const status = error?.response?.status;
	return !status || status === 429 || status >= 500;
};

function waitFor(error, attempt) {
	const header = Number(error?.response?.headers?.['retry-after']);
	if (Number.isFinite(header) && header > 0) return Math.min(header * 1000, MAX_WAIT_MS);
	return BACKOFF_MS[attempt] ?? BACKOFF_MS[BACKOFF_MS.length - 1];
}

/** One call, sent again when it got no answer. */
async function sendOnce(send, body, config, wait) {
	for (let attempt = 0; ; attempt += 1) {
		try {
			// eslint-disable-next-line no-await-in-loop
			return await send(body, config);
		} catch (error) {
			if (attempt >= ATTEMPTS - 1 || !isRetryable(error)) throw error;
			// eslint-disable-next-line no-await-in-loop
			await wait(waitFor(error, attempt));
		}
	}
}

const onlySignature = errors => errors.length > 0 && errors.every(error => error.code === SIGNATURE_CODE);

// A row's plan_limit carries what the 409 body carries, so PlanLimitAlert takes it as it is.
const refusalOf = errors => errors.find(entry => entry.code === PLAN_CODE) || null;

const NO_ANSWER = Object.freeze({ code: 'internal', element: null, message: 'keepr did not answer for this row.' });

const answerRows = answer => (Array.isArray(answer?.rows) ? answer.rows : []);

/**
 * The dry run. Writes nothing.
 *
 * -> { ready, failed: [{ row, errors }], planRefusal, signing }, where `ready`
 * are the rows the commit should send and `signing` says the card wants a
 * signature. Rows the browser could not settle (`row.problems`) are checked
 * too and fail with their other reasons beside them.
 *
 * A dry run counts plan room per call, so once one call meets the limit the
 * next would count from the same room and read as ready. The check stops
 * there: the rows after it are refused with it, not measured.
 */
export async function checkRows({ rows, card, run, send, onProgress, wait = defaultWait, isStopped = () => false }) {
	const ready = [];
	const failed = [];
	let planRefusal = null;
	let signing = false;
	let done = 0;
	const judge = (row, result) => {
		const errors = Array.isArray(result?.errors) ? result.errors : [];
		const refused = result?.status === 'failed' && !onlySignature(errors);
		if (result?.status === 'failed' && !refused) signing = true;
		planRefusal = planRefusal || refusalOf(errors);
		if (!result) failed.push({ row, errors: [NO_ANSWER] });
		else if (refused || row.problems.length) failed.push({ row, errors: refused ? errors : [] });
		else ready.push(row);
	};
	for (const batch of batchesOf(rows)) {
		if (isStopped()) return null;
		if (planRefusal) {
			const refusal = planRefusal;
			batch.forEach(row => failed.push({ row, errors: [refusal] }));
		} else {
			// eslint-disable-next-line no-await-in-loop
			const answers = answerRows(await sendOnce(send, bodyOf(batch, { card, run, dryRun: true }), undefined, wait));
			batch.forEach((row, i) => judge(row, answers.find(entry => entry.index === i)));
		}
		done += batch.length;
		onProgress?.(done, rows.length);
	}
	return { ready, failed, planRefusal, signing };
}

/**
 * The commit. `rows` are the rows the check found ready, or what is left of
 * them. Rows land as their batches do, and what landed stands whatever happens
 * after it, so this answers with everything that happened rather than
 * throwing:
 *
 *   { landed, failed, unsent, planRefusal, stopped, error, signature }
 *
 * `unsent` is what was never sent; sending it through here again finishes the
 * same import. `stopped` is true when `isStopped()` ended it or a signature
 * was declined. `error` is the call that could not be made.
 *
 * A run's external ids are its own, so `duplicate` can only mean this run
 * wrote that row already: the batch landed and its answer was lost.
 *
 * `signature` rides on every call. A card's signature lasts minutes, so a long
 * run can outlive it: a row that answers it wants one asks `sign(count)`, which
 * resolves a new token, or null when the person declines.
 */
export async function commitRows({ rows, card, run, send, onProgress, sign, signature = null, wait = defaultWait, isStopped = () => false }) {
	const out = { landed: 0, failed: [], unsent: [], planRefusal: null, stopped: false, error: null, signature };
	const batches = batchesOf(rows);
	const after = at => batches.slice(at + 1).flat();
	let sent = 0;
	for (let at = 0; at < batches.length; at += 1) {
		let pending = batches[at];
		while (pending.length) {
			if (isStopped()) {
				out.stopped = true;
				out.unsent = [...pending, ...after(at)];
				return out;
			}
			const config = out.signature ? { headers: { 'X-Attestation': out.signature } } : undefined;
			let answers;
			try {
				// eslint-disable-next-line no-await-in-loop
				answers = answerRows(await sendOnce(send, bodyOf(pending, { card, run, dryRun: false }), config, wait));
			} catch (error) {
				out.error = error;
				out.unsent = [...pending, ...after(at)];
				return out;
			}
			const unsigned = [];
			pending.forEach((row, i) => {
				const answer = answers.find(entry => entry.index === i);
				const errors = Array.isArray(answer?.errors) ? answer.errors : [];
				if (answer?.status === 'created') out.landed += 1;
				else if (!answer) out.failed.push({ row, errors: [NO_ANSWER] });
				else if (onlySignature(errors)) unsigned.push(row);
				else if (errors.length && errors.every(error => error.code === 'duplicate')) out.landed += 1;
				else {
					out.failed.push({ row, errors });
					out.planRefusal = out.planRefusal || refusalOf(errors);
				}
			});
			pending = [];
			if (unsigned.length) {
				const left = [...unsigned, ...after(at)];
				// eslint-disable-next-line no-await-in-loop
				const token = sign ? await sign(left.length) : null;
				if (!token) {
					out.stopped = true;
					out.unsent = left;
					return out;
				}
				out.signature = token;
				pending = unsigned;
			}
		}
		sent += batches[at].length;
		onProgress?.(sent, rows.length);
		// Past the plan's limit nothing else can land.
		if (out.planRefusal) {
			out.unsent = after(at);
			return out;
		}
	}
	return out;
}
